/**
 * Service for handling authentication in iframe context
 * Manages postMessage communication between parent window (AIPF Portal) and iframe
 *
 * Key changes:
 * - No API calls to parent domain (eliminates CORS issues)
 * - Portal generates actual bearer token and sends via postMessage
 * - Iframe sets cookie directly with the received token
 */

/* eslint-disable no-console */

import cookie from 'js-cookie';

import { HOSTS } from 'src/configs/hosts';
import { authKey, modalityKey } from 'src/http';
import localStore from 'src/utils/localStore';

// ============================================================================
// Type Definitions
// ============================================================================

export interface AuthCookieData {
  token: string;
  cookieName: string;
  cookieOptions?: {
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'Strict' | 'Lax' | 'None';
  };
}

interface CookieSetOptions {
  path: string;
  secure: boolean;
  sameSite: 'none' | 'lax' | 'strict';
  domain?: string;
  expires?: Date;
}

export interface IframeAuthMessage {
  type:
    | 'SET_AUTH_COOKIE'
    | 'REQUEST_AUTH_TOKEN'
    | 'AUTH_TOKEN_RECEIVED'
    | 'AUTH_TOKEN_ERROR'
    | 'REQUEST_CONTEXT'
    | 'SET_CONTEXT'
    | 'CONTEXT_RECEIVED';
  companyId?: string;
  projectId?: string;
  targetId?: string;
  serviceName?: string;
  metadata?: string;
  token?: string;
  expiresAt?: number;
  cookieName?: string;
  cookieOptions?: AuthCookieData['cookieOptions'];
  error?: string;
  details?: string;
  success?: boolean;
}

export interface ContextData {
  companyId?: string;
  projectId?: string;
  targetId?: string;
  serviceName?: string;
  metadata?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize metadata for comparison (handle both string and object)
 */
const normalizeMetadata = (meta: string | unknown | undefined): string => {
  if (!meta) return '';
  if (typeof meta === 'object') return JSON.stringify(meta);
  if (meta === '[object Object]') return '';
  return meta as string;
};

/**
 * Sets a cookie with iframe-compatible options and verifies it was set
 * @param key - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options (defaults to iframe-safe settings)
 * @returns true if cookie was successfully set, false otherwise
 */
const setIframeSafeCookie = (
  key: string,
  value: string,
  options?: Partial<CookieSetOptions>
): boolean => {
  const defaultOptions: CookieSetOptions = {
    path: '/',
    secure: true,
    sameSite: 'none',
    ...options,
  };

  // Try to set cookie with provided options
  cookie.set(key, value, defaultOptions as cookie.CookieAttributes);

  // Verify cookie was set
  let cookieValue = cookie.get(key);

  // If failed and domain was set, try without domain (fallback for vercel.app, etc.)
  if (!cookieValue && defaultOptions.domain) {
    console.warn(
      `[IframeAuth] Cookie "${key}" with domain failed, retrying without domain`,
      { originalDomain: defaultOptions.domain }
    );
    const optionsWithoutDomain = { ...defaultOptions };
    delete optionsWithoutDomain.domain;
    cookie.set(key, value, optionsWithoutDomain as cookie.CookieAttributes);
    cookieValue = cookie.get(key);
  }

  if (!cookieValue) {
    console.error(`[IframeAuth] ❌ Cookie "${key}" verification failed`, {
      requestedDomain: options?.domain,
      finalDomain: defaultOptions.domain || '(current domain)',
      currentDomain: window.location.hostname,
      secure: defaultOptions.secure,
      sameSite: defaultOptions.sameSite,
      isSecureContext: window.isSecureContext,
      cookiesEnabled: navigator.cookieEnabled,
    });
    return false;
  }

  console.debug(`[IframeAuth] ✅ Cookie "${key}" set and verified`, {
    domain: defaultOptions.domain || '(current domain)',
    options: defaultOptions,
  });
  return true;
};

// ============================================================================
// Implementation
// ============================================================================

class IframeAuthService {
  private allowedOrigins: string[] = [HOSTS.AIPF_PORTAL_DOMAIN];
  private isProcessingAuth: boolean = false;
  private initialized: boolean = false;
  private pendingContext: ContextData | null = null;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private authRequestSent: boolean = false;
  private contextRequestSent: boolean = false;
  private lastContextReceived: string | null = null;
  private initializationComplete: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private initializationResolve: (() => void) | null = null;
  private isLoadingAuth: boolean = false;

  private isOriginAllowed(origin: string): boolean {
    console.debug('[IframeAuth] Checking origin:', {
      incoming: origin,
      allowed: this.allowedOrigins,
    });

    return this.allowedOrigins.some(allowed => {
      if (allowed.includes('*')) {
        const pattern = allowed.replace('*', '.*');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(origin);
      }
      return allowed === origin;
    });
  }

  /**
   * Initialize iframe authentication listener
   */
  public initializeIframeAuth(): void {
    if (this.initialized) return;

    // Create initialization promise
    this.initializationPromise = new Promise<void>(resolve => {
      this.initializationResolve = resolve;
    });

    if (window.self === window.top) {
      console.debug('[IframeAuth] Not in iframe, skipping');
      this.markInitializationComplete();
      return;
    }

    console.debug('[IframeAuth] Initializing', {
      allowedOrigins: this.allowedOrigins,
      currentOrigin: window.location.origin,
    });

    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
    }

    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this.messageHandler);

    this.initialized = true;

    const existingToken = cookie.get(authKey);
    const hasContext = localStore.getAuthzProjectId();

    if (existingToken && hasContext) {
      console.debug('[IframeAuth] ✅ Already authenticated and initialized');
      this.markInitializationComplete();
    } else if (existingToken) {
      console.debug('[IframeAuth] ✅ Authenticated, requesting context');
      this.isLoadingAuth = true;
      this.requestContext();
      // Will complete when context is received
    } else {
      console.debug('[IframeAuth] Requesting authentication');
      this.isLoadingAuth = true;
      this.requestAuthToken();
      // Will complete when auth + context is received
    }
  }

  /**
   * Mark initialization as complete
   */
  private markInitializationComplete(): void {
    if (this.initializationComplete) return;

    this.initializationComplete = true;
    this.isLoadingAuth = false;

    if (this.initializationResolve) {
      this.initializationResolve();
    }

    console.debug('[IframeAuth] ✅ Initialization complete');
  }

  /**
   * Wait for iframe auth initialization to complete
   */
  public async waitForInitialization(): Promise<void> {
    if (this.initializationComplete) {
      return Promise.resolve();
    }

    if (!this.initializationPromise) {
      return Promise.resolve();
    }

    return this.initializationPromise;
  }

  /**
   * Handle incoming postMessage events
   */
  private handleMessage(event: MessageEvent): void {
    // Allow same-origin messages (for webpack HMR in development)
    const isSameOrigin = event.origin === window.location.origin;

    if (!isSameOrigin && !this.isOriginAllowed(event.origin)) {
      console.error('[IframeAuth] Unauthorized origin:', event.origin);
      return;
    }

    if (!event.data || !event.data.type) return;

    const data = event.data as IframeAuthMessage;

    switch (data.type) {
      case 'SET_AUTH_COOKIE':
        this.authRequestSent = false;
        if (this.isProcessingAuth || this.isAuthenticated()) {
          console.warn('[IframeAuth] Ignoring duplicate auth request');
          return;
        }
        this.setAuthCookie(data, event);
        break;

      case 'AUTH_TOKEN_ERROR':
        this.authRequestSent = false;
        this.isProcessingAuth = false;
        this.isLoadingAuth = false;
        console.error(
          '[IframeAuth] Auth token error:',
          data.error,
          data.details
        );
        // Could trigger a user-facing error here if needed
        break;

      case 'SET_CONTEXT':
        this.contextRequestSent = false;

        // Set modality cookie immediately from metadata
        if (data.metadata) {
          try {
            let metadataObj: unknown = null;
            if (typeof data.metadata === 'object') {
              metadataObj = data.metadata;
            } else if (data.metadata !== '[object Object]') {
              metadataObj = JSON.parse(data.metadata);
            }

            if (
              metadataObj &&
              typeof metadataObj === 'object' &&
              'modality' in metadataObj
            ) {
              const { modality } = metadataObj as { modality: unknown };
              if (typeof modality === 'string') {
                console.debug(
                  '[IframeAuth] Modality detected in metadata:',
                  modality
                );
                // Note: Modality cookie will be set in applyContext()
              }
            }
          } catch (error) {
            console.error(
              '[IframeAuth] Failed to parse metadata for modality:',
              error
            );
          }
        }

        const metadataKey =
          typeof data.metadata === 'object'
            ? JSON.stringify(data.metadata)
            : data.metadata;
        const contextKey = `${data.companyId}|${data.projectId}|${data.targetId}|${data.serviceName}|${metadataKey}`;

        if (this.lastContextReceived === contextKey) {
          console.debug(
            '[IframeAuth] Duplicate context detected (in-memory), ignoring'
          );
          return;
        }

        this.lastContextReceived = contextKey;
        this.updateContext(data);
        break;
    }
  }

  /**
   * Update context data
   */
  private updateContext(data: IframeAuthMessage): void {
    const { companyId, projectId, targetId, metadata, serviceName } = data;

    // Store context if auth is pending
    if (this.isProcessingAuth || !this.isAuthenticated()) {
      this.pendingContext = {
        companyId,
        projectId,
        targetId,
        serviceName,
        metadata,
      };
      return;
    }

    // Check if context actually changed
    const existingContext = this.getContext();
    const normalizedNewMetadata = normalizeMetadata(metadata);
    const normalizedExistingMetadata = normalizeMetadata(
      existingContext.metadata
    );

    console.debug('[IframeAuth] Context comparison:', {
      existing: existingContext,
      incoming: { companyId, projectId, targetId, serviceName, metadata },
    });

    // Check if localStorage is empty (no context stored yet)
    const hasStoredContext = !!(
      existingContext.companyId ||
      existingContext.projectId ||
      existingContext.targetId ||
      existingContext.serviceName
    );

    console.debug('[IframeAuth] hasStoredContext:', hasStoredContext);

    const hasChanged =
      existingContext.companyId !== companyId ||
      existingContext.projectId !== projectId ||
      existingContext.targetId !== targetId ||
      existingContext.serviceName !== serviceName ||
      normalizedExistingMetadata !== normalizedNewMetadata;

    console.debug('[IframeAuth] hasChanged:', hasChanged, {
      companyIdChanged: existingContext.companyId !== companyId,
      projectIdChanged: existingContext.projectId !== projectId,
      targetIdChanged: existingContext.targetId !== targetId,
      serviceNameChanged: existingContext.serviceName !== serviceName,
      metadataChanged: normalizedExistingMetadata !== normalizedNewMetadata,
    });

    // If context hasn't changed but localStorage is empty, we still need to apply it
    if (!hasChanged && hasStoredContext) {
      console.debug('[IframeAuth] Context unchanged, skipping reload');

      // Send confirmation to parent
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: 'CONTEXT_RECEIVED', success: true },
          this.allowedOrigins[0] || '*'
        );
      }

      // Mark as complete if we have both auth and context
      this.markInitializationComplete();
      return;
    }

    const isInitialSet = !hasStoredContext;
    console.debug(
      isInitialSet
        ? '[IframeAuth] Setting initial context'
        : '[IframeAuth] Applying context update'
    );

    this.applyContext({
      companyId,
      projectId,
      targetId,
      serviceName,
      metadata,
    });
  }

  /**
   * Apply context and reload
   */
  private applyContext(data: ContextData): void {
    const { companyId, projectId, targetId, metadata, serviceName } = data;

    console.debug('[IframeAuth] Applying context to localStorage:', {
      companyId,
      projectId,
      targetId,
      serviceName,
      metadata: metadata
        ? typeof metadata === 'string'
          ? metadata
          : JSON.stringify(metadata)
        : undefined,
    });

    localStore.setAuthzCompanyId(companyId || '');
    localStore.setAuthzProjectId(projectId || '');
    localStore.setAuthzTargetProjectId(targetId || '');
    localStore.setAuthzServiceName(serviceName || '');

    // Handle metadata - could be string, object, or invalid "[object Object]"
    let metadataStr = '';
    let metadataObj: unknown = null;

    if (metadata) {
      // Check if metadata is already an object (shouldn't happen but be defensive)
      if (typeof metadata === 'object') {
        console.warn('[IframeAuth] Received metadata as object, stringifying');
        metadataStr = JSON.stringify(metadata);
        metadataObj = metadata;
      } else if (metadata === '[object Object]') {
        console.error(
          '[IframeAuth] Received invalid "[object Object]" string for metadata'
        );
        // Don't store invalid metadata
      } else {
        // Normal case: metadata is a valid JSON string
        metadataStr = metadata;
        try {
          metadataObj = JSON.parse(metadata);
        } catch (error) {
          console.error('[IframeAuth] Failed to parse metadata:', error);
        }
      }
    }

    localStore.setAuthzProjectMetadata(metadataStr);

    // Verify that values were set
    console.debug('[IframeAuth] Verified localStorage values:', {
      companyId: localStore.getAuthzCompanyId(),
      projectId: localStore.getAuthzProjectId(),
      targetId: localStore.getAuthzTargetProjectId(),
      serviceName: localStore.getAuthzServiceName(),
      metadata: localStore.getAuthzProjectMetadata(),
    });

    // Set modality cookie if present in metadata, otherwise default to DBT
    let modalitySet = false;
    if (
      metadataObj &&
      typeof metadataObj === 'object' &&
      'modality' in metadataObj
    ) {
      const { modality } = metadataObj as { modality: unknown };
      if (typeof modality === 'string') {
        setIframeSafeCookie(modalityKey, modality.toLowerCase());
        modalitySet = true;
      }
    }

    // If no modality was found in metadata, set DBT as default
    if (!modalitySet) {
      console.debug(
        '[IframeAuth] No modality in metadata, setting default: DBT'
      );
      setIframeSafeCookie(modalityKey, 'dbt');
    }

    // Send confirmation to parent before reload
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        { type: 'CONTEXT_RECEIVED', success: true },
        this.allowedOrigins[0] || '*'
      );
    }

    // Mark initialization complete before reload
    this.markInitializationComplete();

    window.location.reload();
  }

  /**
   * Request storage access for third-party cookies
   */
  private async requestStorageAccess(): Promise<boolean> {
    // Check if Storage Access API is available
    const doc = document as Document & {
      hasStorageAccess?: () => Promise<boolean>;
      requestStorageAccess?: () => Promise<void>;
    };

    if (!doc.hasStorageAccess || !doc.requestStorageAccess) {
      console.debug('[IframeAuth] Storage Access API not available');
      return true; // Assume allowed if API not available
    }

    try {
      // Check if we already have access
      const hasAccess = await doc.hasStorageAccess();
      if (hasAccess) {
        console.debug('[IframeAuth] ✅ Already has storage access');
        return true;
      }

      // Request access - this will show a permission prompt to user
      console.debug('[IframeAuth] Requesting storage access...');
      await doc.requestStorageAccess();
      console.debug('[IframeAuth] ✅ Storage access granted');
      return true;
    } catch (error) {
      console.warn('[IframeAuth] Storage access denied or failed:', error);
      return false;
    }
  }

  /**
   * Set authentication cookie from Portal (no API call needed)
   */
  private async setAuthCookie(
    data: IframeAuthMessage,
    event: MessageEvent
  ): Promise<void> {
    this.isProcessingAuth = true;

    const { token, expiresAt, cookieName, cookieOptions } = data;

    if (!token) {
      console.error('[IframeAuth] Missing token');
      this.isProcessingAuth = false;
      return;
    }

    try {
      // Request storage access first (for third-party cookie support)
      await this.requestStorageAccess();

      console.debug('[IframeAuth] Setting auth cookie from Portal message');

      // Prepare cookie options from Portal message
      const sameSite = cookieOptions?.sameSite
        ? (cookieOptions.sameSite.toLowerCase() as 'none' | 'lax' | 'strict')
        : 'none';
      // SameSite=None requires Secure=true (HTTPS only)
      const secure =
        sameSite === 'none' ? true : cookieOptions?.secure ?? false;

      const finalCookieOptions: Partial<CookieSetOptions> = {
        path: cookieOptions?.path || '/',
        secure,
        sameSite,
      };

      // Set expiration if provided
      if (expiresAt) {
        finalCookieOptions.expires = new Date(expiresAt);
      }

      // Set domain if provided by Portal
      if (cookieOptions?.domain) {
        finalCookieOptions.domain = cookieOptions.domain;
      }

      // Set auth cookie using common function
      const success = setIframeSafeCookie(
        cookieName || authKey,
        token,
        finalCookieOptions
      );

      if (!success) {
        throw new Error('Failed to set authentication cookie');
      }

      // Send confirmation to parent
      if (event.source) {
        (event.source as Window).postMessage(
          { type: 'AUTH_TOKEN_RECEIVED', success: true },
          event.origin
        );
      }

      this.isProcessingAuth = false;

      // Apply pending context or request new one
      if (this.pendingContext) {
        this.applyContext(this.pendingContext);
        this.pendingContext = null;
      } else {
        this.requestContext();
      }
    } catch (error) {
      console.error('[IframeAuth] Error setting auth cookie:', error);
      this.isProcessingAuth = false;
      this.isLoadingAuth = false;
    }
  }

  /**
   * Request auth token from parent
   */
  public requestAuthToken(): void {
    if (!window.parent || window.parent === window) return;

    if (this.authRequestSent) return;

    this.authRequestSent = true;
    window.parent.postMessage({ type: 'REQUEST_AUTH_TOKEN' }, '*');

    setTimeout(() => {
      this.authRequestSent = false;
    }, 5000);
  }

  /**
   * Request context from parent
   */
  public requestContext(): void {
    if (!window.parent || window.parent === window) return;

    if (this.contextRequestSent) return;

    this.contextRequestSent = true;
    window.parent.postMessage({ type: 'REQUEST_CONTEXT' }, '*');

    setTimeout(() => {
      this.contextRequestSent = false;
    }, 5000);
  }

  /**
   * Check if running in iframe
   */
  public isInIframe(): boolean {
    return window.self !== window.top;
  }

  /**
   * Get current context from localStorage
   */
  public getContext(): ContextData {
    return {
      companyId: localStore.getAuthzCompanyId() || undefined,
      projectId: localStore.getAuthzProjectId() || undefined,
      targetId: localStore.getAuthzTargetProjectId() || undefined,
      serviceName: localStore.getAuthzServiceName() || undefined,
      metadata: localStore.getAuthzProjectMetadata() || undefined,
    };
  }

  /**
   * Check if authenticated
   */
  public isAuthenticated(): boolean {
    return !!cookie.get(authKey);
  }

  /**
   * Check if authentication is in progress
   */
  public isLoading(): boolean {
    return this.isLoadingAuth || this.isProcessingAuth;
  }

  /**
   * Retry authentication when token expires
   * Clears existing cookie and requests new auth token from parent
   */
  public retryAuthentication(): void {
    if (!this.isInIframe()) {
      console.debug('[IframeAuth] Not in iframe, cannot retry authentication');
      return;
    }

    console.debug('[IframeAuth] Retrying authentication due to expired token');

    // Clear existing auth cookie
    cookie.remove(authKey);
    cookie.remove(authKey, { path: '/' });
    cookie.remove(authKey, { domain: window.location.hostname });

    // Clear domain-specific cookie if exists
    const domain = window.location.hostname.split('.').slice(-2).join('.');
    cookie.remove(authKey, { domain: `.${domain}`, path: '/' });

    // Reset state
    this.isProcessingAuth = false;
    this.authRequestSent = false;
    this.initializationComplete = false;
    this.isLoadingAuth = true;

    // Request new auth token from parent
    this.requestAuthToken();
  }

  /**
   * Clean up event listeners
   */
  public cleanup(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    this.initialized = false;
    this.isProcessingAuth = false;
    this.authRequestSent = false;
    this.contextRequestSent = false;
    this.pendingContext = null;
    this.lastContextReceived = null;
    this.initializationComplete = false;
    this.initializationPromise = null;
    this.initializationResolve = null;
    this.isLoadingAuth = false;
  }
}

export const iframeAuthService = new IframeAuthService();
export default iframeAuthService;
